import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Timer, Plus, Trash2, Pencil, FileText, X } from "lucide-react";
import { toast } from "sonner";
import { addTMCharge, updateTMCharge, deleteTMCharge, generateTMInvoice } from "@/lib/api";

const CHARGE_TYPES = [
  { value: "labor", label: "Labor" },
  { value: "material", label: "Material" },
  { value: "freight", label: "Freight" },
  { value: "other", label: "Other" },
];

const fmt = (n) => `$${(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const emptyEntry = (laborRate) => ({
  date: new Date().toISOString().slice(0, 10),
  description: "",
  cost_type: "labor",
  quantity: 1,
  rate: laborRate,
  taxable: false,
});

// T&M Charge Log — only rendered on project_type === "tm" work orders.
// PM adds billable charges directly (no change order); charges roll into
// the WO subtotal/tax/total server-side. Any subset of uninvoiced charges
// can be turned into a draft invoice through the standard invoice pipeline.
// `defaultLaborRate` comes from Settings → Default Rates (same billed
// labor rate proposals use); falls back to $155/hr upstream.
export function TMChargesPanel({ woId, form, setForm, defaultLaborRate = 155 }) {
  const charges = form.tm_charges || [];
  const [entry, setEntry] = useState(() => emptyEntry(defaultLaborRate));
  const [editingId, setEditingId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  // Settings load async — refresh the prefilled rate once it arrives
  // (only while the untouched default labor entry is showing).
  useEffect(() => {
    setEntry((e) => (!editingId && e.cost_type === "labor" && !e.description ? { ...e, rate: defaultLaborRate } : e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultLaborRate]);

  const uninvoiced = charges.filter((c) => !c.invoice_id);
  const uninvoicedTotal = uninvoiced.reduce((s, c) => s + (c.total || 0), 0);
  const invoicedTotal = charges.filter((c) => c.invoice_id).reduce((s, c) => s + (c.total || 0), 0);

  const applyResp = (data) => {
    setForm((prev) => ({
      ...prev,
      tm_charges: data.tm_charges ?? prev.tm_charges,
      ...(data.subtotal != null ? { subtotal: data.subtotal } : {}),
      ...(data.tax_amount != null ? { tax_amount: data.tax_amount } : {}),
      ...(data.total != null ? { total: data.total } : {}),
    }));
  };

  const handleTypeChange = (v) => {
    setEntry((e) => ({
      ...e,
      cost_type: v,
      taxable: v !== "labor",
      rate: v === "labor" && (!e.rate || e.cost_type !== "labor") ? defaultLaborRate : e.rate,
    }));
  };

  const handleSubmit = async () => {
    if (!entry.description.trim()) { toast.error("Description is required"); return; }
    setBusy(true);
    try {
      const payload = { ...entry, quantity: parseFloat(entry.quantity) || 0, rate: parseFloat(entry.rate) || 0 };
      const res = editingId
        ? await updateTMCharge(woId, editingId, payload)
        : await addTMCharge(woId, payload);
      applyResp(res.data);
      setEntry(emptyEntry(defaultLaborRate));
      setEditingId(null);
      toast.success(editingId ? "Charge updated" : "Charge added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save charge");
    } finally { setBusy(false); }
  };

  const handleEdit = (c) => {
    setEditingId(c.id);
    setEntry({ date: c.date || "", description: c.description || "", cost_type: c.cost_type || "labor", quantity: c.quantity, rate: c.rate, taxable: !!c.taxable });
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete charge "${c.description}"?`)) return;
    try {
      const res = await deleteTMCharge(woId, c.id);
      applyResp(res.data);
      setSelected((prev) => { const n = new Set(prev); n.delete(c.id); return n; });
      toast.success("Charge deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete charge");
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const allSelected = uninvoiced.length > 0 && uninvoiced.every((c) => selected.has(c.id));
  const toggleSelectAll = () => {
    setSelected(allSelected ? new Set() : new Set(uninvoiced.map((c) => c.id)));
  };

  const selectedTotal = uninvoiced.filter((c) => selected.has(c.id)).reduce((s, c) => s + (c.total || 0), 0);

  const handleInvoice = async () => {
    if (selected.size === 0) { toast.error("Select at least one uninvoiced charge"); return; }
    setBusy(true);
    try {
      const res = await generateTMInvoice(woId, [...selected]);
      applyResp({ tm_charges: res.data.tm_charges });
      setSelected(new Set());
      toast.success(`Invoice ${res.data.invoice?.invoice_number} created (draft)`);
      window.dispatchEvent(new CustomEvent("wo:invoices-refresh"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to generate invoice");
    } finally { setBusy(false); }
  };

  return (
    <div className="border-t pt-4" data-testid="tm-charges-panel">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
          <Timer className="w-4 h-4 text-amber-600" /> T&amp;M Charges
          <span className="text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
            Time &amp; Materials
          </span>
        </h3>
        <Button
          size="sm"
          className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
          disabled={busy || selected.size === 0}
          onClick={handleInvoice}
          data-testid="btn-invoice-tm-charges"
        >
          <FileText className="w-3.5 h-3.5 mr-1" />
          Invoice {selected.size > 0 ? `${selected.size} Charge${selected.size > 1 ? "s" : ""} (${fmt(selectedTotal)})` : "Charges"}
        </Button>
      </div>
      <p className="text-xs text-slate-500 mb-3">
        Add labor, material, and freight as incurred — no change order needed. Check off charges and click
        Invoice to bill them (partially or in full). Labor defaults to ${defaultLaborRate}/hr.
      </p>

      {/* Add / edit charge row */}
      <div className="grid grid-cols-12 gap-2 items-end bg-slate-50 border border-slate-200 rounded-md p-2 mb-3">
        <div className="col-span-2">
          <Label className="text-[10px] text-slate-500">Date</Label>
          <Input type="date" value={entry.date} onChange={(e) => setEntry((v) => ({ ...v, date: e.target.value }))} className="h-8 text-xs" data-testid="input-tm-date" />
        </div>
        <div className="col-span-4">
          <Label className="text-[10px] text-slate-500">Description</Label>
          <Input value={entry.description} onChange={(e) => setEntry((v) => ({ ...v, description: e.target.value }))} placeholder="e.g. RTU-1 troubleshooting — 2 techs" className="h-8 text-xs" data-testid="input-tm-description" />
        </div>
        <div className="col-span-2">
          <Label className="text-[10px] text-slate-500">Type</Label>
          <Select value={entry.cost_type} onValueChange={handleTypeChange}>
            <SelectTrigger className="h-8 text-xs" data-testid="select-tm-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CHARGE_TYPES.map((t) => <SelectItem key={t.value} value={t.value} data-testid={`tm-type-opt-${t.value}`}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-1">
          <Label className="text-[10px] text-slate-500">{entry.cost_type === "labor" ? "Hours" : "Qty"}</Label>
          <Input type="number" step="0.25" value={entry.quantity} onChange={(e) => setEntry((v) => ({ ...v, quantity: e.target.value }))} className="h-8 text-xs text-right" data-testid="input-tm-qty" />
        </div>
        <div className="col-span-1">
          <Label className="text-[10px] text-slate-500">Rate</Label>
          <Input type="number" step="0.01" value={entry.rate} onChange={(e) => setEntry((v) => ({ ...v, rate: e.target.value }))} className="h-8 text-xs text-right" data-testid="input-tm-rate" />
        </div>
        <div className="col-span-1 flex items-center gap-1 pb-2">
          <input
            type="checkbox"
            id="tm-taxable"
            checked={!!entry.taxable}
            disabled={entry.cost_type === "labor"}
            onChange={(e) => setEntry((v) => ({ ...v, taxable: e.target.checked }))}
            className="h-3.5 w-3.5 accent-blue-600"
            data-testid="checkbox-tm-taxable"
          />
          <Label htmlFor="tm-taxable" className="text-[10px] text-slate-500 cursor-pointer">Tax</Label>
        </div>
        <div className="col-span-1 flex gap-1">
          <Button size="sm" className="h-8 text-xs flex-1" disabled={busy} onClick={handleSubmit} data-testid="btn-add-tm-charge">
            {editingId ? "Save" : <><Plus className="w-3.5 h-3.5" /> Add</>}
          </Button>
          {editingId && (
            <Button size="sm" variant="ghost" className="h-8 px-1" onClick={() => { setEditingId(null); setEntry(emptyEntry(defaultLaborRate)); }} data-testid="btn-cancel-tm-edit">
              <X className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Charge table */}
      {charges.length === 0 ? (
        <div className="text-center text-xs text-slate-400 py-3" data-testid="tm-charges-empty">
          No T&amp;M charges yet — add labor or material above as work is performed.
        </div>
      ) : (
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-slate-700 text-white">
              <th className="px-2 py-1.5 w-8">
                <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} className="h-3.5 w-3.5 accent-emerald-500" data-testid="checkbox-tm-select-all" title="Select all uninvoiced" />
              </th>
              <th className="text-left px-2 py-1.5 font-semibold">Date</th>
              <th className="text-left px-2 py-1.5 font-semibold">Description</th>
              <th className="text-left px-2 py-1.5 font-semibold">Type</th>
              <th className="text-right px-2 py-1.5 font-semibold">Qty/Hrs</th>
              <th className="text-right px-2 py-1.5 font-semibold">Rate</th>
              <th className="text-right px-2 py-1.5 font-semibold">Total</th>
              <th className="text-center px-2 py-1.5 font-semibold">Status</th>
              <th className="px-2 py-1.5 w-16"></th>
            </tr>
          </thead>
          <tbody>
            {charges.map((c, i) => (
              <tr key={c.id} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"} data-testid={`tm-charge-row-${c.id}`}>
                <td className="px-2 py-1.5 text-center">
                  {!c.invoice_id && (
                    <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleSelect(c.id)} className="h-3.5 w-3.5 accent-emerald-600" data-testid={`checkbox-tm-select-${c.id}`} />
                  )}
                </td>
                <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap">{c.date}</td>
                <td className="px-2 py-1.5 text-slate-700 font-medium">{c.description}{c.taxable && c.cost_type !== "labor" ? "" : ""}</td>
                <td className="px-2 py-1.5 text-slate-500 capitalize">{c.cost_type}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{c.quantity}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{fmt(c.rate)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums font-medium">{fmt(c.total)}</td>
                <td className="px-2 py-1.5 text-center">
                  {c.invoice_id ? (
                    <span className="text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 whitespace-nowrap" data-testid={`tm-status-invoiced-${c.id}`}>
                      {c.invoice_number || "Invoiced"}
                    </span>
                  ) : (
                    <span className="text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid={`tm-status-uninvoiced-${c.id}`}>
                      Uninvoiced
                    </span>
                  )}
                </td>
                <td className="px-2 py-1.5">
                  {!c.invoice_id && (
                    <div className="flex gap-0.5 justify-end">
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleEdit(c)} data-testid={`btn-edit-tm-${c.id}`}>
                        <Pencil className="w-3 h-3 text-slate-400" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleDelete(c)} data-testid={`btn-delete-tm-${c.id}`}>
                        <Trash2 className="w-3 h-3 text-red-400" />
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {charges.length > 0 && (
        <div className="flex justify-end gap-6 mt-2 text-xs text-slate-500" data-testid="tm-charges-totals">
          <span>Uninvoiced: <span className="font-semibold text-amber-700 tabular-nums">{fmt(uninvoicedTotal)}</span></span>
          <span>Invoiced: <span className="font-semibold text-emerald-700 tabular-nums">{fmt(invoicedTotal)}</span></span>
          <span>All charges: <span className="font-semibold text-slate-700 tabular-nums">{fmt(uninvoicedTotal + invoicedTotal)}</span></span>
        </div>
      )}
    </div>
  );
}
